class ProductSiblingsSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.product-siblings-grid');
    const previous = this.querySelector('[data-siblings-prev]');
    const next = this.querySelector('[data-siblings-next]');
    if (!this.track || !previous || !next) return;

    this.controller = new AbortController();
    const { signal } = this.controller;
    const update = () => {
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      this.toggleAttribute('overflowing', maximum > 1);
      previous.disabled = position <= 1;
      next.disabled = position >= maximum - 1;
    };
    const move = (step) => {
      const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({
        left: step * direction * this.track.clientWidth * 0.8,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };

    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });
    this.track.addEventListener('scroll', update, { signal, passive: true });
    this.resizeObserver = new ResizeObserver(update);
    this.resizeObserver.observe(this.track);
    update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
  }
}

customElements.define('product-siblings-slider', ProductSiblingsSlider);

class SiblingsDrawer extends HTMLElement {
  constructor() {
    super();
    this.bindEvents();
    document.addEventListener('keydown', this.handleEscapeKeyPress.bind(this));
  }

  bindEvents() {
    this.querySelectorAll('[data-drawer-close]').forEach((button) => {
      button.addEventListener('click', this.closeDrawer.bind(this));
    });

    const overlay = this.querySelector('.overlay');
    if (overlay) {
      overlay.addEventListener('click', this.closeDrawer.bind(this));
    }
  }

  openDrawer(siblingsData) {
    this.returnFocus = document.activeElement;
    clearTimeout(this.focusTimer);
    this.classList.add('is-visible');
    document.body.classList.add('overflow-hidden');
    
    // Inject content
    const grid = this.querySelector('[data-siblings-grid]');
    if (grid && siblingsData) {
      grid.innerHTML = siblingsData;
    }
    
    this.focusTimer = setTimeout(() => {
      if (this.classList.contains('is-visible')) this.trapFocus();
    }, 300);
  }

  closeDrawer(event) {
    if (event) {
      event.preventDefault();
    }
    clearTimeout(this.focusTimer);
    this.classList.remove('is-visible');
    document.body.classList.remove('overflow-hidden');
    this.returnFocus?.focus({ preventScroll: true });
  }

  handleEscapeKeyPress(event) {
    if (event.key === 'Escape' && this.classList.contains('is-visible')) {
      this.closeDrawer();
    }
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });

    firstFocusable.focus();
  }
}

customElements.define('siblings-drawer', SiblingsDrawer);

window.openSiblingsDrawer = function(productId, sectionId) {
  const drawer = document.getElementById('siblings-drawer');
  const dataElement = document.querySelector(`[data-siblings-data="${productId}-${sectionId}"]`);
  
  if (drawer && dataElement) {
    const siblingsHTML = dataElement.innerHTML;
    drawer.openDrawer(siblingsHTML);
  }
};

function getMainGalleryWrapper(sectionId) {
  const gallery = document.getElementById(`productmedia-${sectionId}`);
  if (!gallery) return null;
  return gallery.querySelector('[data-main-product-media]');
}

function parseSiblingImages(raw) {
  if (!raw) return [];
  return raw.split('|||').filter(Boolean);
}

function preloadSiblingImages(items) {
  items.forEach((item) => {
    const raw = item.getAttribute('data-hover-images');
    if (!raw) return;
    parseSiblingImages(raw).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  });
}

function swapGalleryImages(wrapper, urls) {
  if (!wrapper || !urls || !urls.length) return;
  const slides = wrapper.querySelectorAll('.swiper-slide[data-media-product]');

  slides.forEach((slide, i) => {
    const img = slide.querySelector('img');
    if (!img) return;
    const src = urls[i] || urls[urls.length - 1];
    img.setAttribute('src', src);
    img.removeAttribute('srcset');
  });
}

function restoreGalleryImages(wrapper, snapshots) {
  if (!wrapper || !snapshots) return;
  const slides = wrapper.querySelectorAll('.swiper-slide[data-media-product]');

  slides.forEach((slide, i) => {
    const img = slide.querySelector('img');
    if (!img || !snapshots[i]) return;
    img.setAttribute('src', snapshots[i].src);
    if (snapshots[i].srcset) {
      img.setAttribute('srcset', snapshots[i].srcset);
    } else {
      img.removeAttribute('srcset');
    }
  });
}

function snapshotGalleryImages(wrapper) {
  if (!wrapper) return [];
  const slides = wrapper.querySelectorAll('.swiper-slide[data-media-product]');
  return Array.from(slides).map((slide) => {
    const img = slide.querySelector('img');
    if (!img) return null;
    return { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
  });
}

function initSiblingHoverEffect() {
  const siblingGrids = document.querySelectorAll('.product-siblings-grid');
  
  siblingGrids.forEach((grid) => {
    if (grid.dataset.hoverInitialized) return;
    const gridAttr = Array.from(grid.attributes).find(attr => attr.name.startsWith('data-siblings-grid-'));
    if (!gridAttr) return;
    
    const sectionId = gridAttr.name.replace('data-siblings-grid-', '');
    const colorDisplay = document.querySelector(`[data-color-display-${sectionId}]`);
    
    if (!colorDisplay) return;
    grid.dataset.hoverInitialized = 'true';
    
    const originalColor = colorDisplay.textContent.trim();
    const nonActiveItems = grid.querySelectorAll('.sibling-item[data-sibling-color]:not(.active)');

    const galleryWrapper = getMainGalleryWrapper(sectionId);
    const originalSnapshots = snapshotGalleryImages(galleryWrapper);

    nonActiveItems.forEach((item) => {
      item.addEventListener('mouseenter', function() {
        if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        preloadSiblingImages([this]);
        const hoverColor = this.getAttribute('data-sibling-color');
        if (hoverColor && colorDisplay) {
          colorDisplay.textContent = hoverColor;
        }

        const raw = this.getAttribute('data-hover-images');
        if (raw && galleryWrapper) {
          const urls = parseSiblingImages(raw);
          swapGalleryImages(galleryWrapper, urls);
        }
      });
      
      item.addEventListener('mouseleave', function() {
        if (colorDisplay) {
          colorDisplay.textContent = originalColor;
        }
        restoreGalleryImages(galleryWrapper, originalSnapshots);
      });
    });
  });
}

document.addEventListener('DOMContentLoaded', initSiblingHoverEffect);

if (document.readyState !== 'loading') {
  setTimeout(initSiblingHoverEffect, 100);
}
